/**
 * keyLevels — Static horizontal reference levels (Wave E.2, v7.5.1
 *             + Top-Down Phase B, v7.5.15).
 *
 * Where `avwap.js` computes ANCHORED VWAPs (trailing averages from a
 * specific bar forward), this module computes STATIC PRICE LEVELS that
 * traders draw as horizontal lines on the chart and watch for reactions:
 *
 *   - Golden Crayon — (PDH + PDL) / 2. A Rake-Trades-popularized name for
 *     the prior-day midpoint. Bull/bear inflection: trade ABOVE it tends
 *     to keep going up; rejection from it tends to roll over. The
 *     2026-05-18 pain point that started Wave F ("missed the clean Golden
 *     Crayon short") refers to this level specifically.
 *
 *   - Prior-day High / Low / Close / Open — classic structural levels.
 *
 *   - Session High / Low — today's intraday extremes so far.
 *
 *   - Today's Open — the first bar of the current trading session.
 *
 *   - All-Time High — lifetime max within the bar window provided. NOTE:
 *     scope is limited to the bars the caller passes in; for true lifetime
 *     ATH, the caller must pass a wide enough bar window. Within an
 *     intraday bar window this is effectively the session high; within a
 *     30-day window it's the 30-day ATH. Top-Down Phase B addition.
 *
 *   - Previous ATH — the highest swing-high pivot before the ATH bar.
 *     When the current bar is making a fresh ATH, this is the prior peak
 *     that's now being tested or just broken. Top-Down Phase B addition.
 *
 *   - Overnight High / Low — extremes during the Globex portion of the
 *     target trading day (6 PM previous day ET → 9:30 AM target day ET).
 *     The pre-RTH range that often acts as support/resistance once
 *     regular trading hours open. Top-Down Phase B addition.
 *
 * Pure function. No side effects. NaN-tolerant (bars with missing fields
 * are skipped, not propagated).
 */

import { getTradingDay, getEtParts } from '../timeService.js';
import { detectSwings } from './swingDetect.js';

/**
 * Group bars by trading day (6pm ET boundary). Returns:
 *   { [dayStr]: { high, low, open, close, firstBarIdx, lastBarIdx } }
 *
 * Pure projection. Bars with missing/invalid OHLC are skipped.
 */
const groupByTradingDay = (bars) => {
  const days = {};
  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    const t = Number(b?.time);
    if (!Number.isFinite(t)) continue;
    const day = getTradingDay(t * 1000);
    if (!day) continue;
    const hi = Number(b.high);
    const lo = Number(b.low);
    const op = Number(b.open);
    const cl = Number(b.close);
    if (![hi, lo, op, cl].every(Number.isFinite)) continue;
    let entry = days[day];
    if (!entry) {
      entry = {
        high: hi,
        low:  lo,
        open: op,
        close: cl,
        firstBarIdx: i,
        lastBarIdx:  i,
      };
      days[day] = entry;
    } else {
      if (hi > entry.high) entry.high = hi;
      if (lo < entry.low)  entry.low  = lo;
      // First bar's open = open. Last bar's close = close.
      entry.close = cl;
      entry.lastBarIdx = i;
    }
  }
  return days;
};

/**
 * Compute the key-levels snapshot for a target trading day.
 *
 * @param {Object} params
 * @param {Array}  params.bars       Bar series spanning at least the prior
 *                                   day + the target day. More days are fine
 *                                   (only the immediately-prior day is used
 *                                   for prior-day levels).
 * @param {string} params.targetDay  YYYY-MM-DD trading day to anchor on.
 *                                   Defaults to the latest day in `bars`.
 *
 * @returns {Object|null}
 *   {
 *     goldenCrayon,  // (PDH + PDL) / 2 — the user's primary inflection level
 *     prevDayHigh,
 *     prevDayLow,
 *     prevDayClose,
 *     prevDayOpen,
 *     sessionHigh,   // today's high so far
 *     sessionLow,    // today's low  so far
 *     todayOpen,     // first bar of the target day
 *     priorDay,      // YYYY-MM-DD of the prior day used (for debugging)
 *     targetDay,     // YYYY-MM-DD of the target day used
 *   }
 *
 *   Individual fields may be null when the underlying day isn't represented
 *   in the bars (e.g., prior-day-* are all null on a one-day bar window).
 *   Returns null when bars[] is empty or no days could be derived.
 */
export const computeKeyLevels = ({ bars, targetDay } = {}) => {
  if (!Array.isArray(bars) || bars.length === 0) return null;

  const grouped = groupByTradingDay(bars);
  const days = Object.keys(grouped).sort();
  if (days.length === 0) return null;

  // Resolve target day.
  const target = targetDay && grouped[targetDay] ? targetDay : days[days.length - 1];
  const targetIdx = days.indexOf(target);
  const prior = targetIdx > 0 ? days[targetIdx - 1] : null;

  const t = grouped[target] || null;
  const p = prior ? (grouped[prior] || null) : null;

  const prevDayHigh  = p ? p.high  : null;
  const prevDayLow   = p ? p.low   : null;
  const prevDayClose = p ? p.close : null;
  const prevDayOpen  = p ? p.open  : null;

  const goldenCrayon = (Number.isFinite(prevDayHigh) && Number.isFinite(prevDayLow))
    ? (prevDayHigh + prevDayLow) / 2
    : null;

  const sessionHigh = t ? t.high  : null;
  const sessionLow  = t ? t.low   : null;
  const todayOpen   = t ? t.open  : null;

  // ── All-Time High + Previous ATH (Top-Down Phase B) ────────────────────
  // ATH: max high across all provided bars. Scope is the bar window the
  // caller provides — not a global instrument-level lifetime stat.
  // Previous ATH: highest swing-high pivot whose bar index is strictly
  // less than the ATH bar's index. Captures "the prior peak before this
  // one" — useful when price is testing or has broken to a fresh extreme.
  let allTimeHigh = null;
  let athBarIdx = -1;
  for (let i = 0; i < bars.length; i++) {
    const h = Number(bars[i]?.high);
    if (!Number.isFinite(h)) continue;
    if (allTimeHigh === null || h > allTimeHigh) {
      allTimeHigh = h;
      athBarIdx = i;
    }
  }
  let previousAth = null;
  if (athBarIdx >= 0) {
    // Use existing swing-detect to find pivot highs, then pick the highest
    // pivot whose bar index is strictly before the ATH bar.
    const swings = detectSwings(bars, { leftBars: 3, rightBars: 3 });
    let bestPrior = null;
    for (const s of swings) {
      if (s.type !== 'high') continue;
      if (!(s.index < athBarIdx)) continue;
      const px = Number(s.price);
      if (!Number.isFinite(px)) continue;
      if (bestPrior === null || px > bestPrior) bestPrior = px;
    }
    previousAth = bestPrior;
  }

  // ── Overnight High / Low (Top-Down Phase B) ────────────────────────────
  // Globex portion of the target trading day: 6 PM previous-calendar-day ET
  // through 9:30 AM target-calendar-day ET. Per the project's 6 PM ET
  // trading-day boundary, this is the leading portion of the trading day.
  //
  // Detection: filter target-day bars by their ET hour-of-day. A bar is
  // "overnight" when its ET hour is in [18, 24) (evening session) OR
  // [0, 9) (overnight) OR (9, 9:30) (pre-RTH lead-in).
  let overnightHigh = null;
  let overnightLow = null;
  if (t) {
    for (let i = t.firstBarIdx; i <= t.lastBarIdx; i++) {
      const b = bars[i];
      const ts = Number(b?.time);
      if (!Number.isFinite(ts)) continue;
      const et = getEtParts(new Date(ts * 1000));
      const totalMinutes = et.hour * 60 + et.minute;
      const inOvernight = totalMinutes >= 18 * 60 || totalMinutes < 9 * 60 + 30;
      if (!inOvernight) continue;
      const hi = Number(b.high);
      const lo = Number(b.low);
      if (Number.isFinite(hi) && (overnightHigh === null || hi > overnightHigh)) overnightHigh = hi;
      if (Number.isFinite(lo) && (overnightLow === null || lo < overnightLow)) overnightLow = lo;
    }
  }

  return {
    goldenCrayon,
    prevDayHigh,
    prevDayLow,
    prevDayClose,
    prevDayOpen,
    sessionHigh,
    sessionLow,
    todayOpen,
    allTimeHigh,
    previousAth,
    overnightHigh,
    overnightLow,
    priorDay:  prior,
    targetDay: target,
  };
};

/**
 * Snapshot helper — given a precomputed keyLevels object, return a flat
 * { name → value } map suitable for proximity checks. Drops null entries.
 *
 * Used by the tradeFlags chip generator and by the per-entry-time
 * snapshotting in maeMfe.
 */
export const keyLevelsAsMap = (kl) => {
  if (!kl || typeof kl !== 'object') return {};
  const out = {};
  const include = (name) => {
    if (Number.isFinite(kl[name])) out[name] = kl[name];
  };
  include('goldenCrayon');
  include('prevDayHigh');
  include('prevDayLow');
  include('prevDayClose');
  include('prevDayOpen');
  include('sessionHigh');
  include('sessionLow');
  include('todayOpen');
  include('allTimeHigh');
  include('previousAth');
  include('overnightHigh');
  include('overnightLow');
  return out;
};

// Test export
export { groupByTradingDay };
